/**
 * 🩸 DETECÇÃO DE VARIZES (negação-consciente)
 *
 * Fonte única para reconhecer, em texto livre de laudo (EDA, ecoendoscopia ou
 * imagem seccional), a presença REAL de varizes gastroesofágicas — sinal
 * definitivo de hipertensão portal clinicamente significativa (CSPH) pelo
 * Baveno VII.
 *
 * Por que existe:
 * - O insight de CSPH (ProntuarioAlertasIA) precisa disparar a sugestão de
 *   carvedilol quando a EDA mostra varizes (mesmo de fino calibre ou gástricas).
 * - A varredura ingênua por "variz" dispararia falso-positivo em laudos
 *   NEGATIVOS ("sem varizes", "varizes esofágicas: ausentes"), levando a
 *   recomendar profilaxia onde a EDA foi normal.
 *
 * Estratégia:
 * - Normaliza acentos e quebra o texto em orações curtas (ponto, ponto-e-vírgula,
 *   quebra de linha e vírgula — NUNCA dois-pontos, para manter "varizes: ausentes"
 *   na mesma oração).
 * - Uma oração conta como POSITIVA quando contém um termo de variz e:
 *     (a) não há negação ANTES do termo na oração ("sem varizes", "ausência de
 *         varizes", "não foram observadas varizes"); e
 *     (b) não há negação-predicado DEPOIS do termo ("varizes ... ausentes",
 *         "varizes não visualizadas").
 * - A assimetria é intencional: "sem" só nega quando vem ANTES do termo, para
 *   não anular "varizes de fino calibre SEM sinais vermelhos" (o "sem" ali
 *   qualifica os sinais vermelhos, não as varizes).
 */

// Termos que denotam varizes propriamente ditas (após remoção de acentos).
// NÃO inclui "ectasia vascular" (GAVE) — não é indicação de NSBB/carvedilol.
const TERMOS_VARIZES = [
  "variz", // cobre variz / varizes
  "varicos", // cobre varicosa(s) / varicoso(s) / varicosidade / cordão varicoso
];

// Negações que, quando aparecem ANTES do termo na mesma oração, o anulam.
const NEGACOES_ANTES = [
  "sem ", // "esofago sem varizes" (com espaço p/ não casar "semelhante")
  "ausen", // ausencia / ausente / ausentes
  "nao ha",
  "nao foram",
  "nao se",
  "nao visualiz",
  "nao identific",
  "nao evidenci",
  "nao observ",
  "negativ",
  "nenhum",
  "inexisten",
];

// Negações-predicado que, DEPOIS do termo na mesma oração, o anulam
// (ex.: "varizes esofagicas: ausentes", "varizes nao visualizadas").
const NEGACOES_DEPOIS = [
  "ausen",
  "nao visualiz",
  "nao identific",
  "nao observ",
  "nao evidenci",
  "negativ",
  "inexisten",
];

function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Retorna true se o texto documenta varizes PRESENTES (não negadas).
 */
export function textoMencionaVarizesPresentes(
  texto?: string | null
): boolean {
  if (!texto) return false;
  const lower = normalizar(texto);

  // Quebra em orações — inclui vírgula, mas NÃO dois-pontos.
  const clausulas = lower.split(/[.;\n]|,\s*/);

  for (const clausula of clausulas) {
    // Índice do primeiro termo de variz na oração.
    let idx = -1;
    for (const termo of TERMOS_VARIZES) {
      const pos = clausula.indexOf(termo);
      if (pos >= 0 && (idx === -1 || pos < idx)) idx = pos;
    }
    if (idx === -1) continue;

    const antes = clausula.slice(0, idx);
    const depois = clausula.slice(idx);

    const negadoAntes = NEGACOES_ANTES.some((n) => antes.includes(n));
    if (negadoAntes) continue;

    const negadoDepois = NEGACOES_DEPOIS.some((n) => depois.includes(n));
    if (negadoDepois) continue;

    return true;
  }

  return false;
}
